import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { MailCheck } from 'lucide-react';
import type { ForgotPasswordRequest } from '@medtrack/shared';

import { api, ApiClientError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InlineError } from '@/components/ui/error-state';
import { AuthLayout } from './AuthLayout';
import { useAuthConfig } from './authConfig';

/**
 * Ask for a reset link.
 *
 * The success screen says the same thing whatever address was typed: "if that
 * email has an account, a link is on its way". The server answers identically
 * for known and unknown addresses so nobody can use this page to find out who
 * has an account, and this page must not undo that by, for example, wording a
 * success differently.
 */
export function ForgotPasswordPage(): JSX.Element {
  const config = useAuthConfig();
  const [email, setEmail] = useState('');

  const mutation = useMutation<{ message: string }, ApiClientError, ForgotPasswordRequest>({
    mutationFn: (body) => api.post<{ message: string }>('/auth/forgot-password', body),
    retry: false,
  });

  const backToLogin = (
    <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
      Back to sign in
    </Link>
  );

  if (config.data && !config.data.passwordReset) {
    return (
      <AuthLayout title="Reset your password" subtitle="Not available on this server" footer={backToLogin}>
        <p className="text-sm text-ink">
          This server is not set up to send emails, so passwords cannot be reset from here. Ask
          the person who runs the app to help you.
        </p>
      </AuthLayout>
    );
  }

  if (mutation.isSuccess) {
    return (
      <AuthLayout title="Check your email" subtitle={email.trim()} footer={backToLogin}>
        <div className="space-y-3 text-sm text-ink">
          <p className="flex items-start gap-2">
            <MailCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-accent" />
            <span>
              If that email has an account, a link to reset your password is on its way. It can take
              a few minutes.
            </span>
          </p>
          <p className="text-muted">
            Not there? Check your spam or junk folder. The link works once and stops working after
            30 minutes.
          </p>
          <Button type="button" variant="secondary" size="md" onClick={() => mutation.reset()}>
            Try a different email
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const fields = mutation.error?.fields;
  const generalError = mutation.error && !mutation.error.isValidation ? mutation.error : null;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate({ email: email.trim() });
  };

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="We will email you a link to choose a new one"
      footer={backToLogin}
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {generalError ? <InlineError error={generalError} /> : null}
        <Input
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fields?.email}
          hint="The email you use to sign in."
          autoComplete="username"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          autoFocus
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={mutation.isPending}
          disabled={email.trim().length === 0}
          className="w-full"
        >
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
